import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { ScrollText, Search } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TextField } from "@/components/ui/text-field";
import { api, createApiQuery } from "@/lib/api";
import {
  ADMIN_PAGE_SIZE,
  adminQueryKeys,
  auditPageToRange,
  type components,
} from "@comandero/shared";
import { useI18n } from "@/providers/i18n-provider";

type ErrorModel = components["schemas"]["ErrorModel"];
type AuditLog = components["schemas"]["AuditLog"];
type AuditListResponse = components["schemas"]["AuditLogListResponse"];

function formatDetails(details: unknown): string {
  if (details === null || details === undefined) {
    return "";
  }
  if (typeof details === "string") {
    return details;
  }
  try {
    return JSON.stringify(details);
  } catch {
    return "";
  }
}

export function AuditLogViewer() {
  const { t } = useI18n();
  const search = useSearch({ from: "/u/$u/admin/audit" });
  const navigate = useNavigate();

  const page = createMemo(() => search().page ?? 1);

  const auditQuery = createApiQuery<AuditListResponse, ErrorModel>(
    () =>
      adminQueryKeys.audit({
        entityType: search().entityType,
        action: search().action,
        userId: search().userId,
        page: page(),
      }),
    async () => {
      const { limit, offset } = auditPageToRange(page());
      return api.GET("/api/admin/audit", {
        params: {
          query: {
            entityType: search().entityType || null,
            action: search().action || null,
            userId: search().userId || null,
            limit,
            offset,
          },
        },
      });
    },
    () => ({ staleTime: 10_000 }),
  );

  const rows = createMemo<AuditLog[]>(() => auditQuery.data?.data ?? []);

  const patchSearch = (patch: Record<string, unknown>) => {
    void navigate({
      to: ".",
      search: (prev) => {
        const next = { ...(prev as Record<string, unknown>), ...patch };
        for (const key of Object.keys(next)) {
          if (next[key] === undefined || next[key] === "") {
            delete next[key];
          }
        }
        return next;
      },
      replace: true,
    });
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <ScrollText class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Audit Log`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(
              msg`Immutable record of sensitive actions across the system — role changes, bans, session revocations, account removals and organization/sharing changes — with who, when and from where. Filter by entity type or action. Read-only.`,
            )}
          </p>
        </div>
      </div>

      <Card class="bg-primary/5 border-primary/10">
        <CardContent class="p-4 grid gap-3 sm:grid-cols-3">
          <div class="relative">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <TextField
              placeholder={t(msg`Entity type`)}
              class="pl-9"
              value={search().entityType ?? ""}
              onInput={(e) => {
                patchSearch({ entityType: e.currentTarget.value, page: undefined });
              }}
            />
          </div>
          <TextField
            placeholder={t(msg`Action`)}
            value={search().action ?? ""}
            onInput={(e) => {
              patchSearch({ action: e.currentTarget.value, page: undefined });
            }}
          />
          <TextField
            placeholder={t(msg`Actor (user id)`)}
            value={search().userId ?? ""}
            onInput={(e) => {
              patchSearch({ userId: e.currentTarget.value, page: undefined });
            }}
          />
        </CardContent>
      </Card>

      <Show
        when={!auditQuery.isLoading}
        fallback={
          <div class="space-y-2">
            <For each={Array.from({ length: 8 }, () => 0)}>
              {() => <Skeleton class="h-14 w-full rounded-lg" />}
            </For>
          </div>
        }
      >
        <Card>
          <CardContent class="p-0 divide-y divide-border/40">
            <For
              each={rows()}
              fallback={
                <div class="p-8 text-center text-sm text-muted-foreground">
                  {t(msg`No audit entries.`)}
                </div>
              }
            >
              {(entry) => (
                <div class="p-4 flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                  <div class="min-w-0 space-y-1">
                    <div class="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary">{entry.action}</Badge>
                      <span class="text-sm font-medium">{entry.entityType}</span>
                      <span class="text-xs text-muted-foreground truncate">{entry.entityId}</span>
                    </div>
                    <div class="text-xs text-muted-foreground">
                      {t(msg`Actor:`)} {entry.userId}
                    </div>
                    <Show when={formatDetails(entry.details)}>
                      <pre class="text-xs text-muted-foreground/80 whitespace-pre-wrap break-all max-w-full">
                        {formatDetails(entry.details)}
                      </pre>
                    </Show>
                  </div>
                  <time class="text-xs text-muted-foreground shrink-0">
                    {new Date(entry.createdAt).toLocaleString()}
                  </time>
                </div>
              )}
            </For>
          </CardContent>
        </Card>

        <div class="flex items-center justify-center gap-4 pt-2">
          <Button
            variant="ghost"
            size="sm"
            disabled={page() <= 1}
            onClick={() => patchSearch({ page: page() - 1 })}
          >
            {t(msg`Previous`)}
          </Button>
          <span class="text-sm text-muted-foreground">
            {(() => {
              const current = page();
              return t(msg`Page ${current}`);
            })()}
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={rows().length < ADMIN_PAGE_SIZE}
            onClick={() => patchSearch({ page: page() + 1 })}
          >
            {t(msg`Next`)}
          </Button>
        </div>
      </Show>
    </div>
  );
}
